import { useEffect, useState, useContext } from 'react';
import { Alert, Box, Button, Divider, Link, Stack, TextField, Typography } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import AuthContext from '../../../context/auth-context';
import { callApiWithData } from '../../../api/call-api';

const LogIn = () => {
  const [loginError, setLoginError] = useState('');
  const [sdkReady, setSdkReady] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const authContext = useContext(AuthContext);

  useEffect(() => {
    if (window.FB) {
      setSdkReady(true);
      return;
    }

    window.fbAsyncInit = () => {
      window.FB.init({
        appId: window.env.FB_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v18.0',
      });
      setSdkReady(true);
    };

    if (!document.getElementById('facebook-sdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-sdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const handleFBLogin = () => {
    window.FB.login(
      (response) => {
        if (response.status === 'connected') {
          handleFBResponse(response.authResponse);
        } else {
          setLoginError('Přihlášení přes Facebook se nepodařilo.');
        }
      },
      { scope: 'email' }
    );
  };

  const handleFBResponse = async (authResponse) => {
    try {
      const response = await callApiWithData('/facebook-auth', {
        accessToken: authResponse.accessToken,
        userId: authResponse.userID,
      });
      authContext.onLogin(response.accessToken);
    } catch (err) {
      setLoginError(err.message || 'Přihlášení se nepodařilo.');
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');

    const endpoint = isRegistering ? '/register' : '/login';
    const data = isRegistering ? { email, name, password } : { email, password };

    try {
      const response = await callApiWithData(endpoint, data, 'POST', false);
      authContext.onLogin(response.accessToken);
    } catch (err) {
      setLoginError(err.message || 'Přihlášení se nepodařilo.');
    }
  };

  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Box sx={{ mb: 3 }}>
        <img src="/make-my-menu.png" alt="Menu Maker" style={{ width: 64, height: 64 }} />
        <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
          Menu Maker
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Naplánuj si, co budeš vařit
        </Typography>
      </Box>

      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {isRegistering ? 'Registrace' : 'Přihlášení'}
        </Typography>

        <Stack component="form" onSubmit={handleEmailSubmit} spacing={1.5} sx={{ maxWidth: 280, mx: 'auto' }}>
          {isRegistering && (
            <TextField
              label="Jméno"
              type="text"
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              inputProps={{ 'data-testid': 'name-input' }}
            />
          )}
          <TextField
            label="E-mail"
            type="email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            inputProps={{ 'data-testid': 'email-input' }}
          />
          <TextField
            label="Heslo"
            type="password"
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            inputProps={{ 'data-testid': 'password-input' }}
          />
          <Button type="submit" variant="contained" sx={{ py: 1.2, textTransform: 'none', fontSize: '0.95rem' }}>
            {isRegistering ? 'Registrovat se' : 'Přihlásit se'}
          </Button>

          <Typography variant="body2">
            {isRegistering ? 'Máte účet? ' : 'Nemáte účet? '}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => {
                setIsRegistering(!isRegistering);
                setLoginError('');
              }}
            >
              {isRegistering ? 'Přihlaste se' : 'Zaregistrujte se'}
            </Link>
          </Typography>
        </Stack>

        <Divider sx={{ my: 2, maxWidth: 280, mx: 'auto' }}>nebo</Divider>

        <Stack spacing={1.5} sx={{ maxWidth: 280, mx: 'auto' }}>
          <Button
            variant="contained"
            startIcon={<FacebookIcon />}
            onClick={handleFBLogin}
            disabled={!sdkReady}
            sx={{
              py: 1.2,
              backgroundColor: '#1877F2',
              color: '#fff',
              '&:hover': { backgroundColor: '#1565C0' },
              textTransform: 'none',
              fontSize: '0.95rem',
            }}
          >
            Přihlášit se přes Facebook
          </Button>
        </Stack>

        {loginError && (
          <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
            {loginError}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default LogIn;
